import React, { useState, useEffect } from 'react';
import { Button, Box } from '@mui/material';
import { setGameSubStatus } from '../services/api';
import AddIcon from '@mui/icons-material/Add';
import PopoverMenu from './common/PopoverMenu';
import { getSubStatusIcon, getSubStatusOptions, toggleSubStatus } from '../utils/subStatus';

interface SubStatusButtonProps {
  gameId: number;
  status: number;
  initialSubStatus: number[];
  onSubStatusChange: (newSubStatus: number[]) => void;
}

const sortNumberArray = (arr: number[]): number[] => {
  return [...arr].sort((a, b) => a - b);
};

const SubStatusButton: React.FC<SubStatusButtonProps> = ({ gameId, status, initialSubStatus, onSubStatusChange }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [subStatus, setSubStatus] = useState<number[]>(sortNumberArray(initialSubStatus));

  useEffect(() => {
    setSubStatus(sortNumberArray(initialSubStatus));
  }, [initialSubStatus]);

  const options = getSubStatusOptions(status);

  // 利用可能なオプションがない場合（status 5 など）は何も表示しない
  if (options.length === 0) {
    return <></>;
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSubStatusChange = async (newSubStatus: number) => {
    try {
      const updatedSubStatus = toggleSubStatus(subStatus, newSubStatus);
      await setGameSubStatus(gameId, updatedSubStatus);
      setSubStatus(updatedSubStatus);
      onSubStatusChange(updatedSubStatus);
    } catch (error) {
      console.error('サブステータスの更新に失敗しました', error);
    }
  };

  const menuOptions = options.map(option => ({
    icon: option.icon,
    label: option.label,
    description: option.description,
    onClick: () => handleSubStatusChange(option.code),
    ...(option.danger ? {
      sx: {
        backgroundColor: 'rgba(211, 47, 47, 0.15)',
        '&:hover': {
          backgroundColor: 'rgba(211, 47, 47, 0.25)',
        }
      },
      iconColor: 'error.main',
    } : {})
  }));

  return (
    <>
      <Button
        onClick={handleClick}
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.01)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '12px',
          padding: { xs: '6px 12px', sm: '8px 12px' },
          lineHeight: 1,
          color: 'text.primary',
          transition: 'all 0.2s ease',
          minWidth: 'auto',
          order: { xs: -1, sm: 0 },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            transform: 'translateY(-1px)',
          },
        }}
      >
        {subStatus.length > 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', lineHeight: 1 }}>
            {subStatus.map((code, index) => (
              <React.Fragment key={code}>
                {index > 0 && (
                  <Box
                    sx={{
                      width: '1px',
                      height: '14px',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      mx: 1
                    }}
                  />
                )}
                <Box sx={{ lineHeight: 0, opacity: 0.9 }}>
                  {getSubStatusIcon(code)}
                </Box>
              </React.Fragment>
            ))}
          </Box>
        ) : (
          <AddIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        )}
      </Button>
      <PopoverMenu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        options={menuOptions}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      />
    </>
  );
};

export default SubStatusButton;
